import React, { useId } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectPlaceholder,
} from '@/shared/ui';
import { clinicDisplayName } from './clinicDisplayName';

interface ClinicScopeOption {
  _id: string;
  name: string;
  shortName?: string;
}

interface ClinicScopeSelectProps {
  clinics: ClinicScopeOption[];
  value: string;
  onChange: (clinicId: string) => void;
  showPicker: boolean;
  scopedClinicName?: string;
  disabled?: boolean;
  error?: string | null;
  /** Field label (picker + locked select) */
  label?: string;
  placeholder?: string;
  /** @deprecated Use `label` */
  scopedLabelPrefix?: string;
}

const ClinicScopeSelect: React.FC<ClinicScopeSelectProps> = ({
  clinics,
  value,
  onChange,
  showPicker,
  scopedClinicName,
  disabled = false,
  error,
  label,
  placeholder,
  scopedLabelPrefix = 'Chi nhánh',
}) => {
  const labelId = useId();
  const fieldLabel = label ?? scopedLabelPrefix;
  const locked = !showPicker;
  const lockedOptions =
    value || scopedClinicName
      ? [
          {
            _id: value || 'scoped',
            name: scopedClinicName ?? (clinicDisplayName(clinics.find((c) => c._id === value)) || value),
          },
        ]
      : clinics;

  return (
    <FormControl
      fullWidth
      required
      error={Boolean(error)}
      disabled={disabled || locked}
    >
      <InputLabel id={labelId}>{fieldLabel}</InputLabel>
      <Select
        labelId={labelId}
        value={value}
        displayEmpty
        sx={{
          '&:not(:hover):not(:focus-within) .MuiSelect-icon': { display: 'none' },
          '&:not(:hover):not(:focus-within) .MuiSelect-select': { pr: '8px !important' },
        }}
        onChange={(e) => onChange(String(e.target.value))}
        renderValue={(selected) => {
          const id = String(selected ?? '');
          if (!id && placeholder) return <SelectPlaceholder>{placeholder}</SelectPlaceholder>;
          const clinic = (locked ? lockedOptions : clinics).find((item) => item._id === id);
          return clinic ? clinicDisplayName(clinic) : id;
        }}
      >
        {placeholder && !locked ? <MenuItem value="">{placeholder}</MenuItem> : null}
        {(locked ? lockedOptions : clinics).map((clinic) => (
          <MenuItem key={clinic._id} value={clinic._id}>
            {clinicDisplayName(clinic)}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default ClinicScopeSelect;
